import React, { useEffect, useMemo, useState } from 'react';
import { Info, Sparkles, Share2, Loader2 } from 'lucide-react';
import { TarotCardData, DeckKey } from '../types/tarot';
import { CardModal } from './CardModal';
import { ModalCloseButton } from './ModalCloseButton';

export type ReadingHistoryEntry = {
  id: string;
  createdAt: string;
  readingType: 'daily' | 'additional';
  cards: TarotCardData[];
  deckKey?: DeckKey;
  question?: string | null;
  insight?: string | null;
  shareUrl?: string | null;
};

interface ReadingHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  readings: ReadingHistoryEntry[];
  onRequestInsight?: (reading: ReadingHistoryEntry) => void;
  onShare?: (reading: ReadingHistoryEntry) => void;
  isLoading?: boolean;
}

const INITIAL_VISIBLE = 10;
const LOAD_STEP = 10;
const MAX_VISIBLE = 30;

const DECK_LABELS: Record<DeckKey, string> = {
  crypto: 'Crypto deck',
  standard: 'Classic deck',
};

const formatDate = (isoDate: string) => {
  try {
    const date = new Date(isoDate);
    return date.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return isoDate;
  }
};

export const ReadingHistoryModal: React.FC<ReadingHistoryModalProps> = ({
  isOpen,
  onClose,
  readings,
  onRequestInsight,
  onShare,
  isLoading = false,
}) => {
  const [visibleCount, setVisibleCount] = useState(() => Math.min(INITIAL_VISIBLE, readings.length));
  const [infoReading, setInfoReading] = useState<ReadingHistoryEntry | null>(null);
  const [previewCard, setPreviewCard] = useState<TarotCardData | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setInfoReading(null);
      setPreviewCard(null);
      return;
    }
  }, [isOpen]);

  useEffect(() => {
    setVisibleCount(Math.min(INITIAL_VISIBLE, readings.length));
  }, [readings]);

  const maxDisplayable = useMemo(() => Math.min(MAX_VISIBLE, readings.length), [readings.length]);
  const visibleReadings = useMemo(() => readings.slice(0, visibleCount), [readings, visibleCount]);
  const canLoadMore = visibleCount < maxDisplayable;

  const handleLoadMore = () => {
    setVisibleCount((prev) => Math.min(prev + LOAD_STEP, maxDisplayable));
  };

  const handleRequestInsight = (reading: ReadingHistoryEntry) => {
    onRequestInsight?.(reading);
    setInfoReading(null);
  };

  const handleShare = (reading: ReadingHistoryEntry) => {
    onShare?.(reading);
    setInfoReading(null);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <section className="relative z-10 flex h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-purple-500/30 bg-slate-950/95">
        <header className="relative flex items-start justify-between border-b border-white/10 px-6 py-5">
          <div>
            <h2 className="crypto-heading text-lg text-white">Reading history</h2>
            <p className="text-sm text-white/60">Preview the last spreads and revisit the story behind each pull.</p>
          </div>
          <ModalCloseButton onClose={onClose} className="text-white/80" />
        </header>

        <div className="relative flex-1 overflow-hidden">
          {isLoading ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-white/70">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span>Loading your readings…</span>
            </div>
          ) : (
            <div className="h-full overflow-y-auto px-6 py-6">
              {visibleReadings.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center text-white/70">
                  <p>No readings yet. Come back after your next spread.</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {visibleReadings.map((reading) => (
                    <button
                      key={reading.id}
                      onClick={() => setInfoReading(reading)}
                      className="relative rounded-2xl border border-white/10 bg-white/5 p-4 text-left transition hover:border-purple-400/40 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/60"
                    >
                      <div className="grid grid-cols-3 gap-3">
                        {reading.cards.map((card) => (
                          <div
                            key={`${reading.id}-${card.id}-${card.orientation}`}
                            className="relative overflow-hidden rounded-xl border border-white/10 bg-black/40 p-1.5"
                          >
                            <div className="relative w-full aspect-[2/3] overflow-hidden rounded-lg">
                              <img
                                src={card.image}
                                alt={card.name}
                                className={`h-full w-full object-cover ${
                                  card.orientation === 'reversed' ? 'rotate-180' : ''
                                }`}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="pointer-events-none absolute top-1 right-1 flex h-7 w-7 items-center justify-center rounded-full bg-black/35 text-white/70 shadow-lg backdrop-blur-sm">
                        <Info className="h-3 w-3" />
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {canLoadMore && (
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={handleLoadMore}
                    className="flex items-center gap-2 rounded-full border border-purple-400/40 bg-purple-500/10 px-5 py-2 text-sm font-semibold text-white transition hover:bg-purple-500/20"
                  >
                    Load more
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {infoReading && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setInfoReading(null)} aria-hidden />
          <div className="relative w-full max-w-md rounded-2xl border border-purple-400/30 bg-slate-900/95 p-6 shadow-xl">
            <ModalCloseButton onClose={() => setInfoReading(null)} className="text-white/80" />
            <div className="space-y-5 text-white pt-6">
              {(() => {
                const deckKey = (infoReading.cards[0]?.deckKey as DeckKey) ?? 'crypto';
                const deckLabel = (DECK_LABELS[deckKey] ?? 'Tarot deck').toUpperCase();
                const formattedTimestamp = formatDate(infoReading.createdAt).toUpperCase();
                return (
                  <div className="space-y-3 pr-12">
                    <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold text-white/60 tracking-[0.25em]">
                      <span>{formattedTimestamp}</span>
                      <span className="text-white/30">•</span>
                      <span>{deckLabel}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {infoReading.cards.map((card) => (
                        <button
                          key={`${infoReading.id}-detail-${card.id}-${card.orientation}`}
                          onClick={() => setPreviewCard(card)}
                          className="group relative overflow-hidden rounded-xl border border-white/10 bg-black/40 p-1.5 transition hover:border-purple-300/60"
                        >
                          <div className="relative w-full aspect-[2/3] overflow-hidden rounded-lg">
                            <img
                              src={card.image}
                              alt={card.name}
                              className={`h-full w-full object-cover transition duration-300 group-hover:scale-[1.05] ${card.orientation === 'reversed' ? 'rotate-180' : ''}`}
                            />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => handleRequestInsight(infoReading)}
                  className="flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:from-purple-400 hover:to-indigo-400"
                >
                  <Sparkles className="h-4 w-4" />
                  Request insight
                </button>
                <button
                  onClick={() => handleShare(infoReading)}
                  className="flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-sm font-semibold text-white/80 transition hover:bg-white/20"
                >
                  <Share2 className="h-4 w-4" />
                  Share reading
                </button>
              </div>

              <div className="space-y-3 text-sm text-white/80">
                {infoReading.question ? (
                  <div className="rounded-lg border border-white/10 bg-black/30 p-3">
                    <p className="text-[11px] uppercase tracking-[0.35em] text-purple-300/70">Question</p>
                    <p className="mt-2 leading-relaxed">{infoReading.question}</p>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-white/10 bg-black/20 p-3 text-white/50">
                    User did not enter a question for this spread.
                  </div>
                )}

                {infoReading.insight ? (
                  <div className="rounded-lg border border-purple-400/20 bg-purple-500/10 p-3">
                    <p className="text-[11px] uppercase tracking-[0.35em] text-purple-200/80">Insight</p>
                    <p className="mt-2 leading-relaxed">{infoReading.insight}</p>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-white/10 bg-black/20 p-3 text-white/50">
                    No insight generated yet — tap “Request insight” to conjure one.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {previewCard && (
        <CardModal
          card={previewCard}
          onClose={() => setPreviewCard(null)}
        />
      )}
    </div>
  );
};
